import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Calendar, Heart, TrendingUp, TrendingDown, Minus, MessageSquare,
  ArrowLeft, AlertTriangle, Star, RefreshCw, ChevronRight, Sun, Cloud, CloudRain
} from 'lucide-react';
import ParentNav from '../components/ParentNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ParentWeeklySummary = () => {
  const navigate = useNavigate();
  const { childId } = useParams();
  
  const [summary, setSummary] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedChild, setSelectedChild] = useState(childId || null);
  const [children, setChildren] = useState([]);

  const getToken = () => localStorage.getItem('parent_token');

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/parent/login');
      return;
    }
    loadData();
  }, [navigate, selectedChild]);

  const loadData = async () => {
    try {
      const token = getToken();
      
      // Load parent dashboard to get children
      const dashboardRes = await axios.get(`${BACKEND_URL}/api/parent/dashboard?token=${token}`);
      setChildren(dashboardRes.data.children || []);
      
      // If no child selected, select first one
      if (!selectedChild && dashboardRes.data.children?.length > 0) {
        setSelectedChild(dashboardRes.data.children[0].id);
        return;
      }
      
      if (selectedChild) {
        let aiDigestApplied = false;
        // Try to load AI-powered weekly digest first
        try {
          const aiDigestRes = await axios.get(
            `${BACKEND_URL}/api/analysis/parent/weekly-digest/${selectedChild}?token=${token}`
          );
          if (aiDigestRes.data?.has_digest) {
            const d = aiDigestRes.data;
            // Map AI-digest shape into the shape the UI renders below
            // (metrics.total_sessions / avg_mood / mood_trend / flags_raised, etc.)
            setSummary({
              child_name: d.child_name,
              week_start: d.week_starting || d.week_ending,
              week_end: d.week_ending,
              summary_text: d.summary,
              is_ai_generated: true,
              metrics: {
                total_sessions: d.sessions_count || 0,
                avg_mood: d.avg_wellbeing != null ? d.avg_wellbeing / 2 : null,
                mood_trend: d.mood_trend || 'stable',
                flags_raised: 0,
              },
              topics_discussed: d.top_topics || [],
              conversation_starters: (d.notable_quotes || []).slice(0, 3),
              focus_areas: d.focus_areas || [],
              improvements: d.improvements || [],
            });
            aiDigestApplied = true;
          }
        } catch (e) {
          // AI digest not available, will try the legacy weekly summary below
        }

        // Fall back to legacy stored weekly summary only if AI digest didn't apply
        if (!aiDigestApplied) {
          try {
            const latestRes = await axios.get(`${BACKEND_URL}/api/parent/weekly-summary/latest/${selectedChild}`);
            if (latestRes.data && !latestRes.data.message) {
              setSummary(latestRes.data);
            }
          } catch (e) {
            // No summary found
          }
        }
        
        // Load all summaries for history
        try {
          const allRes = await axios.get(`${BACKEND_URL}/api/parent/weekly-summaries?child_id=${selectedChild}`);
          setSummaries(allRes.data.summaries || []);
        } catch (e) {
          console.log('No summaries history');
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSummary = async () => {
    if (!selectedChild) return;
    
    try {
      setGenerating(true);
      const token = getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/parent/weekly-summary/generate/${selectedChild}?token=${token}`
      );
      
      if (response.data.success) {
        setSummary(response.data.summary);
        loadData(); // Refresh summaries list
      }
    } catch (error) {
      console.error('Error generating summary:', error);
      alert('Failed to generate summary');
    } finally {
      setGenerating(false);
    }
  };

  const getMoodEmoji = (mood) => {
    const emojis = {
      very_happy: '😄',
      happy: '🙂',
      neutral: '😐',
      sad: '😢',
      very_sad: '😭'
    };
    return emojis[mood] || '😐';
  };

  const getMoodColor = (score) => {
    if (score >= 4) return 'text-green-500';
    if (score >= 3) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'declining': return <TrendingDown className="w-5 h-5 text-red-500" />;
      default: return <Minus className="w-5 h-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ParentNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-rose-50" data-testid="parent-weekly-summary-page">
      <ParentNav />

      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/parent/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-orange-500" />
                  Weekly Digest
                </h1>
                <p className="text-gray-600 text-sm">Your child&apos;s weekly progress report</p>
              </div>
            </div>
          </div>

          {/* Child Selector */}
          {children.length > 1 && (
            <Card className="mb-6">
              <CardContent className="p-4">
                <label className="text-sm text-gray-600 mb-2 block">Select Child</label>
                <div className="flex gap-2 flex-wrap">
                  {children.map((child) => (
                    <Button
                      key={child.id}
                      variant={selectedChild === child.id ? 'default' : 'outline'}
                      onClick={() => setSelectedChild(child.id)}
                      className={selectedChild === child.id ? 'bg-orange-500 hover:bg-orange-600' : ''}
                    >
                      {child.name}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Generate Button */}
          {!summary && selectedChild && (
            <Card className="mb-6">
              <CardContent className="p-8 text-center">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No Summary Available</h3>
                <p className="text-gray-500 mb-4">Generate a weekly summary to see your child&apos;s progress</p>
                <Button
                  onClick={generateSummary}
                  disabled={generating}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                  {generating ? 'Generating...' : 'Generate Weekly Summary'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Summary Content */}
          {summary && (
            <>
              {/* Summary Header */}
              <Card className="mb-6 overflow-hidden">
                <div className="p-6 bg-gradient-to-r from-orange-500 to-rose-500 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold">{summary.child_name}&apos;s Week</h2>
                      <p className="text-orange-100">
                        {summary.week_start} to {summary.week_end}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={generateSummary}
                      disabled={generating}
                      className="text-white border-white hover:bg-white/20"
                    >
                      <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
                
                {/* Metrics */}
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <Calendar className="w-6 h-6 text-orange-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-orange-700">{summary.metrics?.total_sessions || 0}</p>
                      <p className="text-xs text-gray-500">Sessions</p>
                    </div>
                    <div className="text-center p-3 bg-pink-50 rounded-lg">
                      <Heart className="w-6 h-6 text-pink-500 mx-auto mb-1" />
                      <p className={`text-2xl font-bold ${getMoodColor(summary.metrics?.avg_mood || 3)}`}>
                        {summary.metrics?.avg_mood?.toFixed(1) || '—'}
                      </p>
                      <p className="text-xs text-gray-500">Avg Mood</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="w-6 h-6 mx-auto mb-1">
                        {getTrendIcon(summary.metrics?.mood_trend)}
                      </div>
                      <p className="text-lg font-semibold capitalize text-gray-700">
                        {summary.metrics?.mood_trend || 'Stable'}
                      </p>
                      <p className="text-xs text-gray-500">Trend</p>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <AlertTriangle className="w-6 h-6 text-orange-500 mx-auto mb-1" />
                      <p className="text-2xl font-bold text-orange-700">{summary.metrics?.flags_raised || 0}</p>
                      <p className="text-xs text-gray-500">Flags</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Mood Map */}
              {summary.mood_map?.length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-pink-500" />
                      Mood This Week
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between items-end h-32 gap-2">
                      {summary.mood_map.map((entry, idx) => (
                        <div key={idx} className="flex-1 flex flex-col items-center">
                          <span className="text-2xl mb-2">{getMoodEmoji(entry.mood)}</span>
                          <div 
                            className="w-full rounded-t-lg transition-all"
                            style={{ 
                              height: `${(entry.score / 5) * 100}%`,
                              backgroundColor: entry.score >= 4 ? '#22c55e' : 
                                              entry.score >= 3 ? '#eab308' : '#ef4444'
                            }}
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short' })}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Conversation Starters */}
              {summary.conversation_starters?.length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-orange-500" />
                      Conversation Starters
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {summary.conversation_starters.map((starter, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                          <span className="text-2xl">💬</span>
                          <p className="text-gray-700">{starter}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Topics Discussed */}
              {summary.topics_discussed?.length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="w-5 h-5 text-yellow-500" />
                      Topics This Week
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {summary.topics_discussed.map((topic, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-sm border border-yellow-200"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Sessions */}
              {summary.sessions?.length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-orange-500" />
                      Sessions This Week
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {summary.sessions.map((session, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-orange-50 rounded-full flex items-center justify-center">
                              {getMoodEmoji(session.mood_rating >= 4 ? 'happy' : 
                                           session.mood_rating >= 3 ? 'neutral' : 'sad')}
                            </div>
                            <div>
                              <p className="font-medium text-gray-700">
                                {new Date(session.date).toLocaleDateString('en-US', { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </p>
                              <p className="text-sm text-gray-500">
                                with {session.observer_name || 'Observer'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">
                              {session.duration_minutes || 30} min
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Flags Summary */}
              {(summary.flags_summary?.yellow > 0 || summary.flags_summary?.orange > 0 || summary.flags_summary?.red > 0) && (
                <Card className="mb-6 border-orange-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-orange-700">
                      <AlertTriangle className="w-5 h-5" />
                      Safety Flags This Week
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-yellow-50 rounded-lg">
                        <p className="text-2xl font-bold text-yellow-600">{summary.flags_summary?.yellow || 0}</p>
                        <p className="text-sm text-yellow-700">Monitor</p>
                      </div>
                      <div className="text-center p-3 bg-orange-50 rounded-lg">
                        <p className="text-2xl font-bold text-orange-600">{summary.flags_summary?.orange || 0}</p>
                        <p className="text-sm text-orange-700">Concern</p>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{summary.flags_summary?.red || 0}</p>
                        <p className="text-sm text-red-700">Critical</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Previous Summaries */}
          {summaries.length > 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Previous Summaries</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {summaries.slice(1).map((s, idx) => (
                    <div 
                      key={s.id || idx}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                      onClick={() => setSummary(s)}
                    >
                      <div>
                        <p className="font-medium text-gray-700">
                          Week of {s.week_start}
                        </p>
                        <p className="text-sm text-gray-500">
                          {s.metrics?.total_sessions || 0} sessions
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentWeeklySummary;
